/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import * as Sentry from '@sentry/browser';
import { ModelDataStore } from '../../lib/model-data';
import { Constants } from '../../lib/constants';
import { OAUTH_ERRORS, OAuthError } from '../../lib/oauth';
import { IntegrationFlags } from '../../lib/integrations';
import { OAuthIntegrationData } from './data/data';
import { IntegrationFeatures } from './features';
import { RelierAccount, RelierClientInfo } from './relier-interfaces';
import { GenericIntegration, IntegrationType } from './integration';

export enum OAuthPrompt {
  CONSENT = 'consent',
  NONE = 'none',
  LOGIN = 'login',
}

type OAuthIntegrationTypes =
  | IntegrationType.OAuthWeb
  | IntegrationType.OAuthNative
  | IntegrationType.PairingSupplicant
  | IntegrationType.PairingAuthority;

export type SearchParam = IntegrationFlags['searchParam'];

export type OAuthIntegrationOptions = {
  scopedKeysEnabled: boolean;
  scopedKeysValidation: Record<string, any>;
  isPromptNoneEnabled: boolean;
  isPromptNoneEnabledClientIds: Array<string>;
};

/**
 * This integration is used for relying party OAuth implementations. FxA should
 * not send or receive web channel messages if this integration is created.
 *
 * This is a base class for OAuthNativeIntegration.
 */
export class OAuthWebIntegration extends GenericIntegration<
  IntegrationFeatures,
  OAuthIntegrationData
> {
  /** `/oauth/success/:clientId` carries a client id but no OAuth request, so it has no scope. */
  isOAuthSuccessFlow: boolean = false;

  constructor(
    data: ModelDataStore,
    protected readonly storageData: ModelDataStore,
    public readonly opts: OAuthIntegrationOptions,
    type: OAuthIntegrationTypes = IntegrationType.OAuthWeb
  ) {
    super(type, new OAuthIntegrationData(data), {
      allowUidChange: false,
      fxaStatus: false,
      handleSignedInNotification: false,
      reuseExistingSession: true,
      supportsPairing: false,
    });
  }

  getRedirectUri() {
    // clientInfo.redirectUri holds the validated redirect URI. We check that this
    // value exists and throw an error before a call to this function can be made.
    return this.clientInfo?.redirectUri || '';
  }

  getRedirectToRPUrl(
    extraParams: Record<string, number | string | undefined> = {}
  ) {
    const redirectUrl = new URL(this.getRedirectUri());
    const params = {
      ...extraParams,

      action: this.data.action,
      state: this.data.state,

      flowId: this.data.flowId,
      flowBeginTime: this.data.flowBeginTime,
      deviceId: this.data.deviceId,

      utmCampaign: this.data.utmCampaign,
      utmContent: this.data.utmContent,
      utmMedium: this.data.utmMedium,
      utmSource: this.data.utmSource,
      utmTerm: this.data.utmTerm,
    };

    const redirectParams: Record<string, string> = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => {
        return v !== undefined;
      })
    ) as Record<string, string>;

    redirectUrl.search = new URLSearchParams(redirectParams).toString();
    return redirectUrl.toString();
  }

  getRedirectWithErrorUrl(err: {
    response_error_code?: string;
    errno?: number;
  }) {
    return this.getRedirectToRPUrl({
      error: err.response_error_code ?? err.errno,
    });
  }

  restoreOAuthState() {
    const oauth = this.storageData.get('oauth') as any;

    if (typeof oauth === 'object') {
      if (typeof oauth.client_id === 'string') {
        this.data.clientId = oauth.client_id;
      }
      if (typeof oauth.scope === 'string') {
        this.data.scope = oauth.scope;
      }
      if (typeof oauth.state === 'string') {
        this.data.state = oauth.state;
      }
    }
  }

  saveOAuthState() {
    this.storageData.set(
      'oauth',
      JSON.stringify({
        client_id: this.data.clientId,
        scope: this.data.scope,
        state: this.data.state,
      })
    );
    this.storageData.persist();
  }

  getServiceName() {
    const permissions = this.getPermissions();
    // As a special case for UX purposes, any client requesting access to
    // the user's sync data must have a display name of "Firefox Sync".
    // This is also used to check against `integration.isSync()`.
    if (
      permissions.some((permission) =>
        permission.includes(Constants.OAUTH_OLDSYNC_SCOPE)
      )
    ) {
      return Constants.RELIER_SYNC_SERVICE_NAME;
    }

    // If the clientId and the service are the same, prefer the clientInfo
    if (this.data.service && this.data.clientId === this.data.service) {
      if (this.clientInfo?.serviceName) {
        return this.clientInfo.serviceName;
      }
    }

    return super.getServiceName();
  }

  getClientInfo(): RelierClientInfo | undefined {
    return this.clientInfo;
  }

  checkClientInfo() {
    if (this.clientInfoLoadFailed) {
      throw new OAuthError(OAUTH_ERRORS.SERVICE_UNAVAILABLE.errno);
    }
    if (!this.clientInfo?.clientId) {
      throw new OAuthError(OAUTH_ERRORS.UNKNOWN_CLIENT.errno);
    }
  }

  isTrusted() {
    return this.clientInfo?.trusted === true;
  }

  /**
   * Whether the client is known to be untrusted. Not the inverse of
   * `isTrusted()`: an absent `clientInfo` is neither. Gating the consent
   * screen on this keeps a trusted client from being described as untrusted
   * when the client lookup has not resolved.
   */
  isUntrusted() {
    return this.clientInfo?.trusted === false;
  }

  returnOnError() {
    return this.data.returnOnError !== false;
  }

  wantsConsent() {
    return this.data.prompt === OAuthPrompt.CONSENT;
  }

  wantsLogin() {
    // `max_age` used to force a password prompt here (via `maxAge === 0`). Step-up
    // auth must NOT prompt for a password — `max_age` is forwarded to the backend
    // and drives a second-factor challenge instead (RFC 9470 / FXA-12856). Only
    // `prompt=login` still forces password re-entry.
    return this.data.prompt === OAuthPrompt.LOGIN;
  }

  wantsPromptNone() {
    return this.data.prompt === OAuthPrompt.NONE;
  }

  wantsTwoStepAuthentication(): boolean {
    const acrValues = this.data.acrValues;
    if (!acrValues) {
      return false;
    }
    const tokens = acrValues.split(' ');
    return tokens.includes(Constants.TWO_STEP_AUTHENTICATION_ACR);
  }

  getPermissions() {
    if (this.isOAuthSuccessFlow) {
      return [];
    }

    // If the /v1/oauth/client/:id fetch failed during factory initialisation,
    // `isTrusted()` returns false and scope sanitisation would strip every
    // scope a real RP typically asks for, throwing an errno-109 that looks
    // like an RP misconfiguration. Surface the real cause (service
    // unavailable) so the App-level catch renders OAuthDataError without a
    // misleading Sentry capture here — the fetch failure is already logged
    // from useClientInfoState.
    if (this.clientInfoLoadFailed) {
      throw new OAuthError(OAUTH_ERRORS.SERVICE_UNAVAILABLE.errno);
    }

    // Ported from content server, search for _normalizeScopesAndPermissions
    let permissions = Array.from(scopeStrToArray(this.data.scope || ''));

    if (this.isTrusted()) {
      // We have to normalize `profile` into is expanded sub-scopes
      // in order to show the consent screen.
      if (this.wantsConsent()) {
        permissions = replaceItemInArray(
          permissions,
          Constants.OAUTH_TRUSTED_PROFILE_SCOPE,
          Constants.OAUTH_TRUSTED_PROFILE_SCOPE_EXPANSION
        );
      }
    } else {
      permissions = sanitizeUntrustedPermissions(permissions);
    }

    if (!permissions.length) {
      const err = new OAuthError(OAUTH_ERRORS.INVALID_PARAMETER.errno, {
        param: 'scope',
      });
      // capture error to Sentry, but throw so it can be handled
      // in app/index. We check the integration type and present
      // an OAuthDataError component.
      const extra = {
        scope: this.data.scope || 'undefined',
        wantsConsent: this.data.prompt || 'undefined',
        clientId: this.data.clientId || 'undeifned',
        service: this.data.service || 'undefined',
        clientInfo: this.clientInfo || 'undefined',
      };
      err.sentryEventId = Sentry.captureException(err, {
        tags: {
          area: 'OAuthWebIntegration.getPermissions',
          clientId: this.data.clientId,
          service: this.data.service,
        },
        extra: {
          ...extra,
          raw: btoa(JSON.stringify(extra)),
        },
      });
      throw err;
    }

    return permissions;
  }

  getNormalizedScope() {
    const permissions = this.getPermissions();
    return permissions.join(' ');
  }

  protected isPromptNoneEnabledForClient() {
    return (
      this.data.clientId &&
      this.opts.isPromptNoneEnabledClientIds.includes(this.data.clientId)
    );
  }

  async validatePromptNoneRequest(account: RelierAccount): Promise<void> {
    const requestedEmail = this.data.email || this.data.loginHint;

    if (!this.opts.isPromptNoneEnabled) {
      throw new OAuthError('PROMPT_NONE_NOT_ENABLED');
    }

    // If the RP uses email, check they are allowed to use prompt=none.
    // This check is not necessary if the RP uses id_token_hint.
    // See the discussion issue: https://github.com/mozilla/fxa/issues/4963
    if (requestedEmail && !this.isPromptNoneEnabledForClient()) {
      throw new OAuthError('PROMPT_NONE_NOT_ENABLED_FOR_CLIENT');
    }

    if (this._scopeRequestsKeys()) {
      throw new OAuthError('PROMPT_NONE_WITH_KEYS');
    }

    if (account.isDefault() || !account.sessionToken) {
      throw new OAuthError('PROMPT_NONE_NOT_SIGNED_IN');
    }

    // If `id_token_hint` is present, ignore `login_hint` / `email`.
    if (this.data.idTokenHint) {
      let claims: { sub: string };
      try {
        claims = await account.verifyIdToken(
          this.data.idTokenHint,
          this.data.clientId || '',
          Constants.ID_TOKEN_HINT_GRACE_PERIOD
        );
      } catch (err) {
        throw new OAuthError('PROMPT_NONE_INVALID_ID_TOKEN_HINT');
      }

      if (claims.sub !== account.uid) {
        throw new OAuthError('PROMPT_NONE_DIFFERENT_USER_SIGNED_IN');
      }
    }

    if (requestedEmail && requestedEmail !== account.email) {
      throw new OAuthError('PROMPT_NONE_DIFFERENT_USER_SIGNED_IN');
    }
  }

  // Currently only Firefox Sync requires scoped keys, but historically other
  // redirect-based RPs have used them (e.g. Notes). Our functional tests verify
  // this case. When keys are requested, `keys: true` must be sent to the auth
  // server to trigger email verification.
  wantsKeys(): boolean {
    return this._scopeRequestsKeys();
  }

  /**
   * Checks whether any requested scope has scoped keys configured,
   * validating the redirect URI matches. Used by OAuthNativeIntegration's
   * requiresKeys() and wantsKeysIfPasswordEntered(), and by
   * validatePromptNoneRequest() to reject prompt=none with keys.
   */
  protected _scopeRequestsKeys(): boolean {
    if (!this.opts.scopedKeysEnabled) {
      return false;
    }
    if (this.data.keysJwk == null) {
      return false;
    }
    if (!this.data.scope) {
      return false;
    }

    const validation = this.opts.scopedKeysValidation;
    const individualScopes = scopeStrToArray(this.data.scope || '');

    let wantsScopeThatHasKeys = false;
    individualScopes.forEach((scope) => {
      // eslint-disable-next-line no-prototype-builtins
      if (validation.hasOwnProperty(scope)) {
        if (
          validation[scope].redirectUris.includes(this.clientInfo?.redirectUri)
        ) {
          wantsScopeThatHasKeys = true;
        } else {
          const extra = {
            scope,
            redirectUris: validation[scope]?.redirectUris || 'undefined',
            clientInfo: this.clientInfo || 'undefined',
          };
          Sentry.captureMessage(`Invalid redirect parameter`, {
            extra: {
              ...extra,
              raw: btoa(JSON.stringify(extra)),
            },
          });
          throw new Error('Invalid redirect parameter');
        }
      }
    });

    return wantsScopeThatHasKeys;
  }

  getRedirectTo(): string {
    return this.data.redirectTo || '';
  }
}

export function normalizeError(
  err: unknown
): Error | { errno: number; message: string } {
  if (err instanceof Error) {
    return err;
  }
  if (
    typeof err === 'object' &&
    err !== null &&
    'errno' in err &&
    'message' in err
  ) {
    return err as { errno: number; message: string };
  }
  return new Error(String(err));
}

export function scopeStrToArray(scopes: string) {
  const arrScopes = scopes
    .trim()
    .split(/\s+|\++/g)
    .filter((x) => x.length > 0);
  return new Set(arrScopes);
}

export function replaceItemInArray(
  array: Array<string>,
  itemToReplace: string,
  replaceWith: Array<string>
) {
  return Array.from(
    new Set(
      array.map((item) => (item === itemToReplace ? replaceWith : item)).flat()
    )
  );
}

function sanitizeUntrustedPermissions(permissions: Array<string>) {
  return permissions.filter((x) =>
    Constants.OAUTH_UNTRUSTED_ALLOWED_PERMISSIONS.includes(x)
  );
}

export function isOAuthWebIntegration(integration: {
  type: IntegrationType;
}): integration is OAuthWebIntegration {
  return (integration as OAuthWebIntegration).type === IntegrationType.OAuthWeb;
}
